import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import StarIcon from '@mui/icons-material/Star';
import RepeatIcon from '@mui/icons-material/Repeat';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const UNASSIGNED = '#9CA3AF';

/**
 * A single task tile on the weekly board.
 * - Left border + dot = assignee colour (grey if unassigned)
 * - Star = urgent, Repeat = recurring, Paperclip = has attachments
 * - Red treatment when overdue
 * - Arrow buttons move the task ±1 day (keyboard/accessible alternative to drag)
 */
const TaskCard = ({ task, overdue, onOpen, onComplete, onShiftDay, canShiftLeft, canShiftRight,
  selectable, selected, onToggleSelect }) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(task.id), disabled: !!selectable });
  const assignees = task.assignees || [];
  const color = assignees[0]?.color || UNASSIGNED;

  const displayTitle = (() => {
    const title = String(task.title || '').trim();
    const name = String(task.chemist_name || '').trim();
    if (!name) return title;
    const stripped = title.replace(
      new RegExp(`\\s*[—–-]\\s*${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i'), '');
    return stripped || title;
  })();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    borderLeft: `4px solid ${overdue ? '#DC2626' : color}`,
  };

  const stop = (e) => { e.stopPropagation(); };

  const goToPharmacy = (e) => {
    stop(e);
    if (task.chemist_uuid) navigate(`/admin-functions/chemist/${task.chemist_uuid}`);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(selectable ? {} : attributes)}
      {...(selectable ? {} : listeners)}
      onClick={() => (selectable ? onToggleSelect?.(task.id) : onOpen(task))}
      className={`group relative w-full rounded-xl border p-2.5 cursor-pointer transition-all duration-150
        ${selected
          ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200'
          : overdue
            ? 'bg-red-50 border-red-200 hover:border-red-300'
            : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-md'}`}
    >
      <div className="flex items-start gap-1.5">
        {selectable ? (
          <input
            type="checkbox"
            checked={!!selected}
            onClick={stop}
            onChange={() => onToggleSelect?.(task.id)}
            className="flex-shrink-0 mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
          />
        ) : null}

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-snug break-words pr-7 ${overdue ? 'text-red-700' : 'text-gray-800'}`}>
            {task.is_urgent && <StarIcon style={{ fontSize: 14, color: '#F59E0B', verticalAlign: '-2px', marginRight: 2 }} />}
            {displayTitle}
          </p>

          {/* Linked pharmacy — own row, left-aligned, fits the card */}
          {task.chemist_name && (
            <div className="mt-1.5">
              <button
                type="button"
                title={`View ${task.chemist_name}`}
                onPointerDown={stop}
                onClick={goToPharmacy}
                className="flex w-full items-start gap-1 text-[10px] font-semibold leading-snug px-1.5 py-1 rounded-lg bg-sky-50 text-sky-700 border border-sky-100 hover:bg-sky-100 hover:border-sky-300 transition-colors"
              >
                <StorefrontOutlinedIcon style={{ fontSize: 11 }} className="flex-shrink-0 mt-px" />
                <span className="flex-1 min-w-0 text-left break-words">{task.chemist_name}</span>
                <OpenInNewIcon style={{ fontSize: 9 }} className="flex-shrink-0 mt-px opacity-60" />
              </button>
            </div>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {assignees.length === 0 ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                <PersonOutlinedIcon style={{ fontSize: 12 }} /> Unassigned
              </span>
            ) : assignees.length === 1 ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: assignees[0].color || UNASSIGNED }}>
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: assignees[0].color || UNASSIGNED }} />
                {assignees[0].name}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                <span className="flex items-center -space-x-1">
                  {assignees.slice(0, 4).map((a) => (
                    <span key={a.id} title={a.name} className="w-3 h-3 rounded-full ring-2 ring-white inline-block"
                      style={{ backgroundColor: a.color || UNASSIGNED }} />
                  ))}
                </span>
                {assignees.length} people
              </span>
            )}
            {task.is_recurring && (
              <span title="Recurring task" className="text-gray-400"><RepeatIcon style={{ fontSize: 13 }} /></span>
            )}
            {task.attachment_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                <AttachFileIcon style={{ fontSize: 12 }} />{task.attachment_count}
              </span>
            )}
            {overdue && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                Overdue
              </span>
            )}
          </div>
        </div>
      </div>

      {!selectable && (
        <button
          title="Mark complete"
          onPointerDown={stop}
          onClick={(e) => { stop(e); onComplete(task); }}
          className="absolute top-1.5 right-1.5 z-10 flex items-center justify-center w-6 h-6 rounded-full
            bg-white border border-gray-200 text-gray-400 shadow-sm
            hover:bg-green-600 hover:border-green-600 hover:text-white transition"
        >
          <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
        </button>
      )}

      {/* Day-shift arrows — appear on hover */}
      <div className="absolute inset-y-0 left-0 right-0 pointer-events-none flex items-center justify-between px-0.5 opacity-0 group-hover:opacity-100 transition">
        <button
          title="Move to previous day"
          disabled={!canShiftLeft}
          onPointerDown={stop}
          onClick={(e) => { stop(e); onShiftDay(task, -1); }}
          className="pointer-events-auto bg-white/90 shadow rounded-full p-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-0"
        >
          <ChevronLeftIcon style={{ fontSize: 16 }} />
        </button>
        <button
          title="Move to next day"
          disabled={!canShiftRight}
          onPointerDown={stop}
          onClick={(e) => { stop(e); onShiftDay(task, 1); }}
          className="pointer-events-auto bg-white/90 shadow rounded-full p-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-0"
        >
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
};

export default TaskCard;
